import { TFunction } from '@bonanza-social/i18n';
import dayjs, { Dayjs } from 'dayjs';
import duration, { Duration } from 'dayjs/plugin/duration';

import {
  HOUR_MILLISECOND,
  MIN_MILLISECONDS,
  SECOND_MILLISECOND,
} from './Timer.constants';
import { DisplayMode, UnitSize } from './Timer.types';

dayjs.extend(duration);

export const getInterval = (targetTime: Dayjs) => {
  const diff = targetTime.diff(dayjs(), 'minutes');

  const isLessThanHour = diff < 60;
  if (isLessThanHour) {
    return SECOND_MILLISECOND;
  }

  const isLessThanDay = diff < 1440;
  if (isLessThanDay) {
    return MIN_MILLISECONDS;
  }

  return HOUR_MILLISECOND;
};

const getLocKey = (key: string, unitSize: UnitSize) => {
  return unitSize === 'short' ? `${key}_short` : key;
};

export const getFormat = (
  timeBetween: Duration,
  unitSize: UnitSize,
  displayMode: DisplayMode,
  t: TFunction,
) => {
  const months = `${t(getLocKey('month', unitSize), {
    count: timeBetween.months(),
  })}`;

  const days = `${t(getLocKey('day', unitSize), {
    count: timeBetween.days(),
  })}`;

  const hours = `${t(getLocKey('hour', unitSize), {
    count: timeBetween.hours(),
  })}`;

  const minutes = `${t(getLocKey('minute', unitSize), {
    count: timeBetween.minutes(),
  })}`;

  const seconds = `${t(getLocKey('second', unitSize), {
    count: timeBetween.seconds(),
  })}`;

  if (timeBetween.months()) {
    return displayMode === 'precise' ? `${months} ${days}` : `${months}`;
  }

  if (timeBetween.days()) {
    return displayMode === 'precise' ? `${days} ${hours}` : `${days}`;
  }

  if (timeBetween.hours()) {
    return displayMode === 'precise' ? `${hours} ${minutes}` : `${hours}`;
  }

  if (timeBetween.minutes()) {
    return displayMode === 'precise' ? `${minutes} ${seconds}` : `${minutes}`;
  }

  if (timeBetween.seconds()) {
    return `${seconds}`;
  }

  // TODO: Possibly add fallback here
  return;
};

export const getClockFormat = (timeBetween: moment.Duration): string => {
  const padTimeUnit = (value: number): string => String(value).padStart(2, '0');

  const months = timeBetween.months();
  const days = timeBetween.days();
  const hours = timeBetween.hours();
  const minutes = timeBetween.minutes();
  const seconds = timeBetween.seconds();

  const paddedMonths = padTimeUnit(months);
  const paddedDays = padTimeUnit(days);
  const paddedHours = padTimeUnit(hours);
  const paddedMinutes = padTimeUnit(minutes);
  const paddedSeconds = padTimeUnit(seconds);

  const resultParts: string[] = [];

  if (months > 0 || days > 0) {
    if (months > 0) resultParts.push(paddedMonths);
    if (months > 0 || days > 0) resultParts.push(paddedDays);
  }

  if (hours > 0 || resultParts.length > 0) {
    resultParts.push(paddedHours);
  }

  resultParts.push(paddedMinutes);
  resultParts.push(paddedSeconds);

  return resultParts.join(':');
};

export default { getFormat, getInterval };
